import React, { useMemo, useRef, useState, useEffect } from 'react';
import { GENERATORS, PALETTES, PALETTE_MAP } from '../../../constants/genArt';
import { renderThumbnail, renderGenerator } from '../../../utils/genArtRenderers';
import { renderNormiePortraitAsync } from '../../../utils/genArtRenderers/normiePortrait';
import { preparePalette } from '../../../utils/genArtRenderers/index';

// Render at half-production resolution then downscale — patterns look
// proportional to the main preview instead of oversized in small thumbs.
const RENDER_W = 540, RENDER_H = 675;
const THUMB_W = 192, THUMB_H = 240;

function renderScaledThumb(genId, paletteId, seed) {
  const big = document.createElement('canvas');
  big.width = RENDER_W;
  big.height = RENDER_H;
  const bigCtx = big.getContext('2d');
  renderGenerator(bigCtx, RENDER_W, RENDER_H, {
    type: genId, paletteId, seed,
    hueShift: 0, saturation: 100, brightness: 100,
  });
  const small = document.createElement('canvas');
  small.width = THUMB_W;
  small.height = THUMB_H;
  small.getContext('2d').drawImage(big, 0, 0, THUMB_W, THUMB_H);
  return small.toDataURL('image/jpeg', 0.85);
}

/** Async thumbnail for normiePortrait — loads Normie #42 as the preview */
async function renderNormieThumbAsync(paletteId) {
  const big = document.createElement('canvas');
  big.width = RENDER_W;
  big.height = RENDER_H;
  const bigCtx = big.getContext('2d');
  const rawPalette = PALETTE_MAP[paletteId];
  if (!rawPalette) return null;
  const palette = preparePalette(rawPalette, 0, 1, 1);
  palette.renderMode = rawPalette.renderMode;
  await renderNormiePortraitAsync(bigCtx, RENDER_W, RENDER_H, palette, { tokenId: 4354 }, 0);
  const small = document.createElement('canvas');
  small.width = THUMB_W;
  small.height = THUMB_H;
  small.getContext('2d').drawImage(big, 0, 0, THUMB_W, THUMB_H);
  return small.toDataURL('image/jpeg', 0.85);
}

/**
 * Creative messages for empty generator slots — in the spirit of
 * building beautiful generative art backgrounds.
 */
const EMPTY_MESSAGES = [
  'Coming\nSoon',
  'Dream\nMore',
  'New Art\nBrewing',
  'Stay\nTuned',
  'More\nMagic',
  'Pixels\nAwaiting',
  'Next\nWave',
];

/**
 * Generator grid — always 7 built-in screens like a physical device.
 * Shows thumbnail previews for generators, loading animation while
 * rendering, and creative placeholders for empty slots.
 */
export function GenArtGeneratorGrid({ family, selectedType, paletteId, seed, onSelect }) {
  const thumbnailCacheRef = useRef({});
  const [thumbnails, setThumbnails] = useState({});
  const [loading, setLoading] = useState({});

  // Filter generators by family
  const generators = useMemo(
    () => GENERATORS.filter(g => g.family === family),
    [family]
  );

  // Always 7 slots — pad with empty placeholders
  const TOTAL_SLOTS = 7;
  const emptySlots = TOTAL_SLOTS - generators.length;

  // Family accent color
  const familyAccent = useMemo(() => {
    const accents = {
      organic: '#52b788',
      flow: '#60c8f0',
      geo: '#FF9500',
      poster: '#E63946',
      strata: '#8d6e63',
      normies: '#48494b',
    };
    return accents[family] || '#52b788';
  }, [family]);

  // Pre-render thumbnails with loading state — re-renders when palette changes
  useEffect(() => {
    const cache = thumbnailCacheRef.current;
    let cancelled = false;

    async function renderAll() {
      // Use the selected palette, fall back to family default
      const activePaletteId = paletteId
        || (PALETTES.find(p => p.family === (family === 'flow' ? 'organic' : family)) || PALETTES[0]).id;

      for (const gen of generators) {
        const cacheKey = `${gen.id}_${activePaletteId}_${seed}`;
        if (cache[cacheKey]) {
          if (!cancelled) setThumbnails(prev => ({ ...prev, [gen.id]: cache[cacheKey] }));
          continue;
        }

        // Mark as loading
        if (!cancelled) setLoading(prev => ({ ...prev, [gen.id]: true }));

        try {
          let dataUrl;
          if (gen.family === 'normies') {
            // Async thumbnail — loads actual Normie image
            dataUrl = await renderNormieThumbAsync(activePaletteId);
          } else {
            dataUrl = renderScaledThumb(gen.id, activePaletteId, seed);
          }
          if (!cancelled && dataUrl) {
            cache[cacheKey] = dataUrl;
            setThumbnails(prev => ({ ...prev, [gen.id]: dataUrl }));
          }
        } catch (e) {
          // Fallback to direct small render
          try {
            const fallback = renderThumbnail(gen.id, activePaletteId, seed);
            if (!cancelled && fallback) {
              cache[cacheKey] = fallback;
              setThumbnails(prev => ({ ...prev, [gen.id]: fallback }));
            }
          } catch (e2) {
            console.warn(`[GenArt] Thumbnail failed for ${gen.id}:`, e2);
          }
        }

        // Clear loading
        if (!cancelled) setLoading(prev => ({ ...prev, [gen.id]: false }));
      }
    }

    renderAll();
    return () => { cancelled = true; };
  }, [family, generators, paletteId, seed]);

  return (
    <div>
      <div className="text-[10px] text-white/40 mb-1.5 uppercase tracking-wider">Generator</div>
      <div className="genart-generator-grid">
        {generators.map(gen => {
          const isActive = gen.id === selectedType;
          const thumb = thumbnails[gen.id];
          const isLoading = loading[gen.id] && !thumb;

          return (
            <button
              key={gen.id}
              className={`genart-generator-btn${isActive ? ' genart-generator-btn-active' : ''}`}
              onClick={() => onSelect(gen.id)}
              title={gen.desc}
            >
              {/* LED dot */}
              <div
                className={`genart-generator-led${isActive ? ' genart-generator-led-active' : ''}`}
                style={isActive ? {
                  background: familyAccent,
                  '--led-color': familyAccent,
                } : undefined}
              />
              {/* Thumbnail / loading */}
              <div className="genart-generator-thumb">
                {isLoading && (
                  <div className="genart-generator-loading">
                    <div className="genart-generator-loading-bar" />
                  </div>
                )}
                {thumb && (
                  <img
                    src={thumb}
                    alt=""
                    draggable={false}
                    className="genart-generator-thumb-img"
                  />
                )}
              </div>
              {/* Label */}
              <div className="genart-generator-label">{gen.name}</div>
            </button>
          );
        })}
        {/* Empty slots — device screens with creative messages */}
        {Array.from({ length: emptySlots }, (_, i) => (
          <div key={`empty-${i}`} className="genart-generator-btn genart-generator-btn-empty">
            <div className="genart-generator-led" />
            <div className="genart-generator-thumb">
              <div className="genart-generator-empty-screen">
                <span className="genart-generator-empty-text">
                  {EMPTY_MESSAGES[i % EMPTY_MESSAGES.length]}
                </span>
              </div>
            </div>
            <div className="genart-generator-label" style={{ opacity: 0.15 }}>—</div>
          </div>
        ))}
      </div>
    </div>
  );
}
